import { Button, Input, Modal, Space, Switch } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DetailHeader as ZhDetailHeader } from "zhihao-ui-react";

import request, { type CustomAxiosRequestConfig } from "@/api";
import DetailJson, { type DetailJsonBlock } from "@/views/components/detailJson";
import { useUserStore } from "@/views/stores";
import { confirmDialog } from "@/utils/confirmDialog";
import { ADMIN_PUBLISH_PLAT, NOTIFICATION_TYPE, PUBLISH_STATUS } from "@zhihaoscm/meta";
import type { NotificationItem } from "./types";
import { getNotificationActionErrorMessage } from "./notificationError";
import "./NotificationPage.scss";

export interface NotificationDetailPageProps {
    loadNotification?: (id: number | string) => Promise<NotificationItem>;
    resolveContent?: (content: string) => Promise<string>;
    permissions?: string[];
    publishNotification?: (id: number | string, payload: { publishTime: string }) => Promise<unknown>;
    unpublishNotification?: (id: number | string) => Promise<unknown>;
    revokeNotification?: (id: number | string) => Promise<unknown>;
}

/** 复用 Vue 通知详情接口。 */
export async function loadNotificationDetail(id: number | string): Promise<NotificationItem> {
    const response = await request.service.get<NotificationItem>(`/dataService/admin/notification/${id}`);
    return response.data;
}

/** 沿用 Vue 通知发布接口及 publishTime payload。 */
export async function publishNotificationDetail(
    id: number | string,
    payload: { publishTime: string }
): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/notification/publish/${id}`, payload, {
        // 详情页会展示接口失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
    return response.data;
}

/** 沿用 Vue 取消发布接口。 */
export async function unpublishNotificationDetail(id: number | string): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/notification/unpublished/${id}`, undefined, {
        // 详情页会展示接口失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
    return response.data;
}

/** 沿用 Vue 撤回接口。 */
export async function revokeNotificationDetail(id: number | string): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/notification/revoke/${id}`, undefined, {
        // 详情页会展示接口失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
    return response.data;
}

/** 复用 Vue editorManage 的媒体地址回填规则，保持通知正文接口契约。 */
export async function resolveNotificationContent(content: string): Promise<string> {
    const rawContent = content.replace(/^==ids=(.*?)\n/, "");
    const mediaPattern = /<(img|video|source)\s+[^>]*src=["']([^"']+)["']/g;
    const mediaSources = Array.from(rawContent.matchAll(mediaPattern)).map(match => match[2]);
    const mediaIds = Array.from(
        new Set(mediaSources.map(source => Number(source.match(/#(\d+)$/)?.[1])).filter(Boolean))
    );
    if (!mediaIds.length) return rawContent;
    const response = await request.service.post<Array<{ id: number; path: string }>>(
        "/dataService/admin/file/ids",
        mediaIds
    );
    let resolved = rawContent;
    mediaSources.forEach(source => {
        const id = Number(source.match(/#(\d+)$/)?.[1]);
        const file = response.data.find(item => item.id === id);
        if (file) resolved = resolved.replaceAll(source, `${file.path}#${file.id}`);
    });
    return resolved;
}

/** 将 Vue 通知详情返回的分类、状态和发布端枚举统一为数字。 */
function normalizeNotificationDetail(item: NotificationItem): NotificationItem {
    return {
        ...item,
        notification: {
            ...item.notification,
            classify: item.notification.classify == null ? undefined : Number(item.notification.classify),
            state: item.notification.state == null ? undefined : Number(item.notification.state),
            publishPlatInfo: item.notification.publishPlatInfo?.map(value => Number(value))
        }
    };
}

/** 按旧 Vue headerPanel 的格式保留通知时间展示。 */
function formatNotificationTime(value?: string): string {
    return value == null ? "-" : value.replace("T", " ").slice(0, 16);
}

/** 使用旧 Vue detailJson 的发布端元数据生成展示文案。 */
function getPublishPlatformText(values?: number[]): string {
    return (values ?? [])
        .map(value => ADMIN_PUBLISH_PLAT.getIntroByCode(value))
        .filter((label): label is string => Boolean(label))
        .join("、");
}

/** 使用旧 Vue 通知分类元数据生成详情文案。 */
function getNotificationTypeText(value?: number): string {
    return NOTIFICATION_TYPE.getIntroByCode(value) || "-";
}

/** 将 Vue 发布状态元数据转换为详情头部文案。 */
function getPublishStatusText(value?: number): string {
    return PUBLISH_STATUS.getIntroByCode(value) || "-";
}

/**
 * DetailHeader 会把空字符串归一为 "--"；用空节点承载旧 Vue 的可见空值，且不改变全局组件语义。
 */
function preserveNotificationHeaderEmptyValue(value?: string) {
    return value === "" ? <span aria-label="空创建人" /> : (value ?? "-");
}

/** 复用 Vue 发布弹窗规则：开启定时发布时默认填充当前时间后五分钟。 */
function getDefaultScheduledPublishValues(): { date: string; time: string } {
    const scheduled = new Date(Date.now() + 5 * 60 * 1000);
    const pad = (value: number) => String(value).padStart(2, "0");
    return {
        date: `${scheduled.getFullYear()}-${pad(scheduled.getMonth() + 1)}-${pad(scheduled.getDate())}`,
        time: `${pad(scheduled.getHours())}:${pad(scheduled.getMinutes())}`
    };
}

export function NotificationDetailPage({
    loadNotification = loadNotificationDetail,
    resolveContent = resolveNotificationContent,
    permissions,
    publishNotification = publishNotificationDetail,
    unpublishNotification = unpublishNotificationDetail,
    revokeNotification = revokeNotificationDetail
}: NotificationDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，详情页通知写操作保留入口但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const id = new URLSearchParams(location.search).get("id");
    const [item, setItem] = useState<NotificationItem | null>(null);
    const [resolvedContent, setResolvedContent] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [operation, setOperation] = useState<"publish" | null>(null);
    const [timedPublish, setTimedPublish] = useState(false);
    const [publishDate, setPublishDate] = useState("");
    const [publishTime, setPublishTime] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!id) return;
        void loadNotification(id)
            .then(async result => {
                const normalized = normalizeNotificationDetail(result);
                // 复用旧 Vue editorManage，移除富文本文件 ID 前缀并回填图片/视频真实地址。
                const content = await resolveContent(normalized.notification.content ?? "");
                setItem(normalized);
                setResolvedContent(content);
            })
            .catch(() => setErrorMessage("通知详情加载失败"));
    }, [id, loadNotification, resolveContent]);

    const notification = item?.notification;
    const canWrite = currentPermissions.includes("ROLE_SS_NOTICE_W");

    /** 旧 Vue 只有发布操作需要确认弹窗，并在每次打开时清空定时发布草稿。 */
    const openPublishOperation = () => {
        setOperation("publish");
        setTimedPublish(false);
        setPublishDate("");
        setPublishTime("");
        setErrorMessage(null);
    };

    /** 按 Vue LOG_DATE_FORMAT_T 生成定时发布时间。 */
    const getScheduledPublishTime = (): string => {
        if (!publishDate || !publishTime) return "";
        return `${publishDate}T${publishTime.length === 5 ? `${publishTime}:00` : publishTime}`;
    };

    /** 保持 Vue Switch 切换行为，开启时给出可直接提交的默认发布时间。 */
    const handleTimedPublishChange = (enabled: boolean) => {
        setTimedPublish(enabled);
        if (enabled) {
            const defaults = getDefaultScheduledPublishValues();
            setPublishDate(defaults.date);
            setPublishTime(defaults.time);
        } else {
            setPublishDate("");
            setPublishTime("");
        }
    };

    /** 发布成功沿用旧 Vue router.back，返回用户进入详情前的页面。 */
    const submitPublishOperation = async () => {
        if (!id || operation !== "publish") return;
        if (timedPublish && !getScheduledPublishTime()) {
            setErrorMessage("请选择发布时间");
            return;
        }
        setSubmitting(true);
        try {
            await publishNotification(Number(id), {
                publishTime: timedPublish ? getScheduledPublishTime() : ""
            });
            setOperation(null);
            setErrorMessage(null);
            void navigate(-1);
        } catch (error) {
            setErrorMessage(getNotificationActionErrorMessage(error, "通知状态操作失败"));
        } finally {
            setSubmitting(false);
        }
    };

    /** 执行取消发布或已确认的撤回，成功后统一返回通知列表。 */
    const submitDirectOperation = async (nextOperation: "unpublish" | "revoke") => {
        if (!id) return false;
        setSubmitting(true);
        setErrorMessage(null);
        try {
            if (nextOperation === "unpublish") await unpublishNotification(Number(id));
            else await revokeNotification(Number(id));
            void navigate("/userOperation/notificationManage");
            return true;
        } catch (error) {
            setErrorMessage(getNotificationActionErrorMessage(error, "通知状态操作失败"));
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    /** 复用旧 Vue MessageBox 确认撤回；失败时保留详情页和再次确认入口。 */
    const confirmRevoke = async () => {
        if (!(await confirmDialog("是否撤回？"))) return;
        await submitDirectOperation("revoke");
    };

    // 旧 Vue 由 DetailJson 明确切成头部、基本字段、内容三张白色卡片。
    const detailColumns: DetailJsonBlock[] = [
        {
            list: [{ rowSlot: "headerSlot", span: 4 }]
        },
        {
            list: [
                { label: "分类", value: getNotificationTypeText(notification?.classify), span: 4 },
                { label: "标题", value: notification?.title ?? "-", span: 4 },
                { label: "发布端", value: getPublishPlatformText(notification?.publishPlatInfo), span: 4 }
            ]
        },
        {
            sectionTitle: "内容",
            list: [{ rowSlot: "contentSlot", span: 4 }]
        }
    ];

    return (
        <section
            aria-label="notification-detail-page"
            className="notification-page detail-page-transparent flex h-full min-h-full flex-col"
        >
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {notification ? (
                <>
                    <DetailJson
                        columns={detailColumns}
                        slots={{
                            headerSlot: () => (
                                <ZhDetailHeader
                                    rightInfos={[
                                        {
                                            label: "创建人",
                                            value: preserveNotificationHeaderEmptyValue(item?.user?.name)
                                        },
                                        ...(notification.state !== PUBLISH_STATUS.DRAFT.code
                                            ? [
                                                  {
                                                      label: "发布时间",
                                                      value: formatNotificationTime(notification.publishTime)
                                                  }
                                              ]
                                            : []),
                                        {
                                            label: "更新时间",
                                            value: formatNotificationTime(notification.editTime)
                                        }
                                    ]}
                                    stateDesc={getPublishStatusText(notification.state)}
                                    title="平台通知详情"
                                />
                            ),
                            contentSlot: () => (
                                <div
                                    aria-label="通知内容"
                                    className="max-w-3xl [&_img]:max-w-full [&_video]:max-w-full"
                                    // Vue 使用 v-html 展示 editorManage 还原后的可信通知富文本。
                                    dangerouslySetInnerHTML={{ __html: resolvedContent }}
                                />
                            )
                        }}
                    />
                    <Space className="operation-fixed-btn" size={0}>
                        {notification.state === PUBLISH_STATUS.DRAFT.code && canWrite ? (
                            <Button
                                aria-label="发布"
                                disabled={banButton}
                                onClick={openPublishOperation}
                                type="primary"
                            >
                                发布
                            </Button>
                        ) : null}
                        {notification.state === PUBLISH_STATUS.PENDING.code && canWrite ? (
                            <Button
                                aria-label="取消发布"
                                disabled={banButton}
                                loading={submitting}
                                onClick={() => void submitDirectOperation("unpublish")}
                                type="primary"
                            >
                                取消发布
                            </Button>
                        ) : null}
                        {notification.state === PUBLISH_STATUS.DRAFT.code && canWrite ? (
                            <Button
                                disabled={banButton}
                                onClick={() =>
                                    void navigate(`/userOperation/NotificationEdit?id=${notification.id || ""}`)
                                }
                            >
                                编辑
                            </Button>
                        ) : null}
                        {notification.state === PUBLISH_STATUS.PUBLISHED.code && canWrite ? (
                            <Button
                                aria-label="撤回"
                                disabled={banButton}
                                loading={submitting}
                                onClick={() => void confirmRevoke()}
                                type="primary"
                            >
                                撤回
                            </Button>
                        ) : null}
                        <Button onClick={() => void navigate("/userOperation/notificationManage")}>返回</Button>
                    </Space>
                </>
            ) : null}
            <Modal
                centered
                className="notification-publish-modal"
                open={operation !== null}
                title="是否发布"
                width={400}
                styles={{ body: { minHeight: 100, paddingTop: 17 } }}
                onCancel={() => setOperation(null)}
                footer={
                    <Space>
                        <Button onClick={() => setOperation(null)}>取消</Button>
                        <Button
                            aria-label="确认发布"
                            loading={submitting}
                            onClick={() => void submitPublishOperation()}
                            type="primary"
                        >
                            发布
                        </Button>
                    </Space>
                }
            >
                <div className="notification-publish-dialog__row">
                    <span className="notification-publish-dialog__label">定时发布</span>
                    <Switch aria-label="定时发布" checked={timedPublish} onChange={handleTimedPublishChange} />
                </div>
                {timedPublish ? (
                    <Space className="notification-publish-dialog__date-time">
                        <Input
                            aria-label="发布时间日期"
                            type="date"
                            value={publishDate}
                            onChange={event => setPublishDate(event.target.value)}
                        />
                        <Input
                            aria-label="发布时间时间"
                            type="time"
                            value={publishTime}
                            onChange={event => setPublishTime(event.target.value)}
                        />
                    </Space>
                ) : null}
            </Modal>
        </section>
    );
}

export default NotificationDetailPage;
